/**
 * 师资分析页面
 */

import React, { useState, useEffect } from 'react';
import {
  Card,
  Row,
  Col,
  Statistic,
  Table,
  Spin,
  message,
  Typography,
  Divider,
  Space,
  Tag
} from 'antd';
import {
  UserOutlined,
  TeamOutlined,
  CrownOutlined,
  EnvironmentOutlined,
  SafetyCertificateOutlined,
  DollarOutlined
} from '@ant-design/icons';
import facultyService, { FacultyAnalytics } from '../services/facultyService';

const { Title } = Typography;

const FacultyAnalyticsPage: React.FC = () => {
  const [analytics, setAnalytics] = useState<FacultyAnalytics | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadAnalytics();
  }, []);

  /**
   * 加载分析数据
   */
  const loadAnalytics = async () => {
    setLoading(true);
    try {
      const data = await facultyService.getAnalytics();
      setAnalytics(data);
    } catch (error: any) {
      message.error(error.message || '加载分析数据失败');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div style={{ padding: '24px', textAlign: 'center' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!analytics) {
    return null;
  }

  // 类别分布表格列配置
  const categoryColumns = [
    {
      title: '师资类别',
      dataIndex: 'category',
      key: 'category',
      render: (category: string) => <Tag color="blue">{category}</Tag>
    },
    {
      title: '人数',
      dataIndex: 'count',
      key: 'count',
      render: (count: number) => (
        <Statistic
          value={count}
          valueStyle={{ fontSize: 16 }}
        />
      )
    },
    {
      title: '占比',
      dataIndex: 'percentage',
      key: 'percentage',
      render: (percentage: number) => `${percentage.toFixed(1)}%`
    }
  ];

  // 地区分布表格列配置
  const regionColumns = [
    {
      title: '地区',
      dataIndex: 'region',
      key: 'region',
      render: (region: string) => (
        <Space>
          <EnvironmentOutlined />
          {region}
        </Space>
      )
    },
    {
      title: '人数',
      dataIndex: 'count',
      key: 'count',
      render: (count: number) => (
        <Statistic
          value={count}
          valueStyle={{ fontSize: 16 }}
        />
      )
    },
    {
      title: '占比',
      dataIndex: 'percentage',
      key: 'percentage',
      render: (percentage: number) => `${percentage.toFixed(1)}%`
    }
  ];

  // 证书类别分布表格列配置
  const certificateColumns = [
    {
      title: '证书类别',
      dataIndex: 'certificate_category',
      key: 'certificate_category',
      render: (category: string) =>
        category ? <Tag color="green">{category}</Tag> : <Tag color="default">暂无</Tag>
    },
    {
      title: '人数',
      dataIndex: 'count',
      key: 'count',
      render: (count: number) => (
        <Statistic
          value={count}
          valueStyle={{ fontSize: 16 }}
        />
      )
    },
    {
      title: '占比',
      dataIndex: 'percentage',
      key: 'percentage',
      render: (percentage: number) => `${percentage.toFixed(1)}%`
    }
  ];

  return (
    <div style={{ padding: '24px' }}>
      <Title level={2}>师资数据分析</Title>

      {/* 概览统计卡片 */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="师资总数"
              value={analytics.total_faculty}
              prefix={<UserOutlined />}
              valueStyle={{ color: '#3f8600' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="师资类别数"
              value={analytics.total_categories}
              prefix={<TeamOutlined />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="覆盖地区数"
              value={analytics.total_regions}
              prefix={<EnvironmentOutlined />}
              valueStyle={{ color: '#722ed1' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="持证师资数"
              value={analytics.certified_faculty}
              prefix={<SafetyCertificateOutlined />}
              valueStyle={{ color: '#cf1322' }}
            />
          </Card>
        </Col>
      </Row>

      {/* 费用统计 */}
      {analytics.avg_daily_fee && (
        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
          <Col xs={24} sm={12} md={8}>
            <Card>
              <Statistic
                title="平均课酬(元/天)"
                value={analytics.avg_daily_fee}
                prefix={<DollarOutlined />}
                precision={0}
                valueStyle={{ color: '#52c41a' }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={8}>
            <Card>
              <Statistic
                title="最高课酬(元/天)"
                value={analytics.max_daily_fee || 0}
                prefix={<CrownOutlined />}
                precision={0}
                valueStyle={{ color: '#fa8c16' }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={8}>
            <Card>
              <Statistic
                title="最低课酬(元/天)"
                value={analytics.min_daily_fee || 0}
                prefix={<DollarOutlined />}
                precision={0}
                valueStyle={{ color: '#1890ff' }}
              />
            </Card>
          </Col>
        </Row>
      )}

      <Divider />

      {/* 详细分析表格 */}
      <Row gutter={[16, 16]}>
        {/* 师资类别分布 */}
        <Col xs={24} lg={8}>
          <Card
            title={
              <Space>
                <TeamOutlined />
                <span>师资类别分布</span>
              </Space>
            }
          >
            <Table
              columns={categoryColumns}
              dataSource={analytics.category_distribution.map((item, index) => ({
                ...item,
                key: index,
                percentage: (item.count / analytics.total_faculty) * 100
              }))}
              pagination={false}
              size="small"
            />
          </Card>
        </Col>

        {/* 地区分布 */}
        <Col xs={24} lg={8}>
          <Card
            title={
              <Space>
                <EnvironmentOutlined />
                <span>地区分布</span>
              </Space>
            }
          >
            <Table
              columns={regionColumns}
              dataSource={analytics.region_distribution.map((item, index) => ({
                ...item,
                key: index,
                percentage: (item.count / analytics.total_faculty) * 100
              }))}
              pagination={false}
              size="small"
              scroll={{ y: 300 }}
            />
          </Card>
        </Col>

        {/* 证书类别分布 */}
        <Col xs={24} lg={8}>
          <Card
            title={
              <Space>
                <SafetyCertificateOutlined />
                <span>证书类别分布</span>
              </Space>
            }
          >
            <Table
              columns={certificateColumns}
              dataSource={analytics.certificate_distribution.map((item, index) => ({
                ...item,
                key: index,
                percentage: (item.count / analytics.total_faculty) * 100
              }))}
              pagination={false}
              size="small"
            />
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default FacultyAnalyticsPage;